import { useState, useEffect } from 'react';
import { Invoice, InvoiceItem } from '@/data/mockData';
import { useProcurement } from '@/context/ProcurementContext';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Trash2, Save, FileText, Package, DollarSign, X } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/utils';

// Define valid variants for Badge component
type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';

interface InvoiceFormProps {
  invoice?: Invoice;
  onSave: (invoiceData: Partial<Invoice>) => Promise<boolean>;
  onCancel: () => void;
}

export const InvoiceForm = ({ invoice, onSave, onCancel }: InvoiceFormProps) => {
  const { state } = useProcurement();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  // Form data
  const [formData, setFormData] = useState({
    invoiceNumber: '',
    poNumber: '',
    poId: 0,
    vendorId: 0,
    vendorName: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    status: 'Created' as 'Created' | 'Pending' | 'Approved' | 'Rejected' | 'Fully Received' | 'Partially Received' | 'Handed Over',
    receivedStatus: 'Not Received' as 'Not Received' | 'Partially Received' | 'Fully Received' | 'Normal',
    poQuantity: 0,
    receivedQuantity: 0,
    invoicedQuantity: 0,
    pendingQuantity: 0,
    poAmount: 0,
    amount: 0,
    subtotal: 0,
    taxAmount: 0,
    totalAmount: 0,
    notes: '',
    paymentTerms: '',
    items: [] as InvoiceItem[],
  });


  // Initialize form data
  useEffect(() => {
    if (invoice) {
      setFormData({
        invoiceNumber: invoice.invoiceNumber,
        poNumber: invoice.poNumber,
        poId: Number((invoice as any).poId) || 0,
        vendorId: invoice.vendorId,
        vendorName: invoice.vendorName,
        invoiceDate: invoice.invoiceDate.split('T')[0],
        issueDate: (invoice as any).issueDate?.split('T')[0] || new Date().toISOString().split('T')[0],
        dueDate: (invoice as any).dueDate || '',
        status: invoice.status,
        receivedStatus: (invoice as any).receivedStatus || 'Not Received',
        poQuantity: Number(invoice.poQuantity) || 0,
        receivedQuantity: Number(invoice.receivedQuantity) || 0,
        invoicedQuantity: Number(invoice.invoicedQuantity) || 0,
        pendingQuantity: Number((invoice as any).pendingQuantity) || 0,
        poAmount: Number((invoice as any).poAmount) || 0,
        amount: Number((invoice as any).amount) || 0,
        subtotal: invoice.subtotal,
        taxAmount: invoice.taxAmount,
        totalAmount: invoice.totalAmount,
        notes: invoice.notes || '',
        paymentTerms: (invoice as any).paymentTerms || '',
        items: invoice.items || [],
      });
    } else {
      // Generate new invoice number
      const newNumber = `INV-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`;
      setFormData((prev) => ({
        ...prev,
        invoiceNumber: newNumber,
      }));
    }
  }, [invoice]);

  // Invoice status should always be 'Created' after creation
  useEffect(() => {
    // Set invoice status to 'Created' for new invoices
    if (!invoice) {
      setFormData((prev) => ({ 
        ...prev, 
        status: 'Created',
        receivedStatus: 'Not Received'
      }));
    }
  }, [invoice]);

  // Get available POs - show all POs for invoice creation
  const availablePOs = state.purchaseOrders;

  // Get SKUs for selected PO
  const selectedPOItems = formData.poNumber
    ? state.purchaseOrders.find((po) => po.poNumber === formData.poNumber)?.items || []
    : [];


  // Get existing invoices for the selected PO - only show invoices for POs that actually exist
  const existingInvoicesForPO = formData.poNumber
    ? state.invoices.filter(invoice => 
        invoice.poNumber === formData.poNumber && 
        state.purchaseOrders.some(po => po.poNumber === formData.poNumber)
      )
    : [];

  // Calculate total invoiced quantity for this PO
  const totalInvoicedQuantity = existingInvoicesForPO.reduce((sum, invoice) => {
    return sum + (invoice.invoicedQuantity || 0);
  }, 0);

  // Calculate remaining quantity that can be invoiced
  const selectedPO = formData.poNumber
    ? state.purchaseOrders.find((po) => po.poNumber === formData.poNumber)
    : null;
  
  const totalPOQuantity = selectedPO
    ? selectedPO.items.reduce((sum, item) => sum + item.quantity, 0)
    : 0;
  
  const remainingQuantity = totalPOQuantity - totalInvoicedQuantity;

  // Add item from PO
  const addItemFromPO = (poItem: any) => {
    const sku = state.skus.find((s) => s.id === poItem.skuId);
    if (!sku) return;

    const newItem: InvoiceItem = {
      id: Date.now() + Math.random(),
      skuId: poItem.skuId,
      skuCode: sku.skuCode,
      skuName: sku.name,
      description: sku.description || '',
      quantity: poItem.quantity,
      unitPrice: poItem.unitPrice,
      totalPrice: poItem.quantity * poItem.unitPrice,
    };

    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, newItem],
    }));
  };

  // Add all items from PO at once
  const addAllItemsFromPO = () => {
    const newItems: InvoiceItem[] = selectedPOItems
      .filter((poItem) => !formData.items.some((item) => item.skuId === poItem.skuId))
      .map((poItem) => {
        const sku = state.skus.find((s) => s.id === poItem.skuId);
        if (!sku) return null;
        
        return {
          id: Date.now() + Math.random(), // Ensure unique IDs
          skuId: poItem.skuId,
          skuCode: sku.skuCode,
          skuName: sku.name,
          description: sku.description || '',
          quantity: poItem.quantity,
          unitPrice: poItem.unitPrice,
          totalPrice: poItem.quantity * poItem.unitPrice,
        };
      })
      .filter((item): item is InvoiceItem => item !== null);

    if (newItems.length > 0) {
      setFormData((prev) => ({
        ...prev,
        items: [...prev.items, ...newItems],
      }));
    }
  };

  // Add custom item
  const addCustomItem = () => {
    const newItem: InvoiceItem = {
      id: Date.now() + Math.random(),
      skuId: 0,
      skuCode: '',
      skuName: '',
      description: '',
      quantity: 1,
      unitPrice: 0,
      totalPrice: 0,
    };

    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, newItem],
    }));
  };

  // Update item
  const updateItem = (itemId: number, field: keyof InvoiceItem, value: any) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.id === itemId ? { ...item, [field]: value } : item
      ),
    }));
  };

  // Update item with calculated total price
  const updateItemWithTotal = (itemId: number, updates: Partial<InvoiceItem>) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.map((item) => {
        if (item.id === itemId) {
          const updatedItem = { ...item, ...updates };
          // Recalculate total price if quantity or unitPrice changed
          if (updates.quantity !== undefined || updates.unitPrice !== undefined) {
            updatedItem.totalPrice = updatedItem.quantity * updatedItem.unitPrice;
          }
          return updatedItem;
        }
        return item;
      }),
    }));
  };

  // Remove item
  const removeItem = (itemId: number) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.id !== itemId),
    }));
  };

  // Calculate totals based on received items (invoiced quantity)
  useEffect(() => {
    // Calculate subtotal based on invoiced quantity, not full PO quantity
    const subtotal = formData.items.reduce((sum, item) => {
      // Use the actual quantity from items (which represents invoiced quantity)
      const itemTotal = item.quantity * item.unitPrice;
      return sum + itemTotal;
    }, 0);
    
    const taxRate = 0.1; // 10% tax rate
    const taxAmount = subtotal * taxRate;
    const totalAmount = subtotal + taxAmount;

    // Update invoiced quantity based on actual items
    const calculatedInvoicedQuantity = formData.items.reduce((sum, item) => sum + item.quantity, 0);

    setFormData((prev) => ({
      ...prev,
      subtotal,
      taxAmount,
      totalAmount,
      invoicedQuantity: calculatedInvoicedQuantity,
    }));
  }, [formData.items]);

  // Handle form submission
  const handleSubmit = async () => {
    try {
      // Validate required fields
      if (!formData.vendorId) {
        toast.error('Please select a vendor');
        return;
      }

      if (!formData.poNumber) {
        toast.error('Please select a Purchase Order');
        return;
      }

      if (!formData.invoiceDate) {
        toast.error('Please enter an invoice date');
        return;
      }

      if (formData.items.length === 0) {
        toast.error('Please add at least one item to the invoice');
      return;
    }

    setIsSubmitting(true);

    // Calculate final invoiced quantity from items
      const finalInvoicedQuantity = formData.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
      
      if (finalInvoicedQuantity <= 0) {
        toast.error('Invoice quantity must be greater than 0');
        setIsSubmitting(false);
        return;
      }
    
    // Calculate total received quantity for this PO (including this invoice)
    const totalReceivedForPO = totalInvoicedQuantity + finalInvoicedQuantity;
    
    // Implement PO status logic based on your exact requirements
    let newPOStatus = '';
    if (totalReceivedForPO === totalPOQuantity) {
      newPOStatus = 'FULLY_RECEIVED';
    } else if (totalReceivedForPO < totalPOQuantity) {
      newPOStatus = 'PARTLY_RECEIVED';
    } else if (totalReceivedForPO > totalPOQuantity) {
      newPOStatus = 'OVER_INVOICED';
    } else {
      newPOStatus = 'CREATED';
    }

    // Validate dates before parsing
    const validateAndParseDate = (dateString: string): string => {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        throw new Error(`Invalid date format: ${dateString}`);
      }
      return date.toISOString();
    };

    const invoiceData = {
      ...formData,
      invoicedQuantity: finalInvoicedQuantity,
      invoiceDate: validateAndParseDate(formData.invoiceDate),
      issueDate: validateAndParseDate(formData.issueDate),
      dueDate: formData.dueDate ? validateAndParseDate(formData.dueDate) : '',
      createdAt: invoice?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      // Add metadata for PO status update
      _shouldUpdatePOStatus: true,
      _newPOStatus: newPOStatus,
      _totalReceivedForPO: totalReceivedForPO,
      _totalPOQuantity: totalPOQuantity,
    };

    const success = await onSave(invoiceData);
    setIsSubmitting(false);

    if (success) {
      toast.success('Invoice saved successfully');
      }
    } catch (error) {
      console.error('Error saving invoice:', error);
      toast.error('Failed to save invoice. Please try again.');
      setIsSubmitting(false);
    }
  };


  return (
    <Dialog open={true} onOpenChange={onCancel}>
      <DialogContent className="max-w-6xl h-[90vh] p-0 overflow-hidden">
        <div className="flex flex-col h-full min-h-0">
          {/* Fixed Header */}
          <div className="shrink-0 sticky top-0 z-10 bg-background border-b p-4">
            <div className="flex items-start justify-between">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {invoice ? 'Edit Invoice' : 'Create Invoice'}
          </DialogTitle>
          <DialogDescription>
            {invoice ? 'Update the invoice details' : 'Create a new invoice for a purchase order'}
          </DialogDescription>
        </DialogHeader>
              <button
                aria-label="Close"
                onClick={onCancel}
                className="ml-4 inline-flex h-8 w-8 items-center justify-center rounded-md border hover:bg-muted"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Scrollable Body */}
          <div className="flex-1 min-h-0 overflow-y-auto max-h-[75vh] px-6 pb-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="summary">Summary</TabsTrigger>
          </TabsList>

          {/* Basic Information Tab */}
          <TabsContent value="basic" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Invoice Details</CardTitle>
                <CardDescription>Basic information about the invoice</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="invoiceNumber">Invoice Number *</Label>
                    <Input
                      id="invoiceNumber"
                      value={formData.invoiceNumber}
                      onChange={(e) => setFormData((prev) => ({ ...prev, invoiceNumber: e.target.value }))}
                      disabled
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="poNumber">Purchase Order</Label>
                    <Select
                      value={formData.poNumber}
                      onValueChange={(value) => {
                        const po = state.purchaseOrders.find((p) => p.poNumber === value);
                        setFormData((prev) => ({
                          ...prev,
                          poNumber: value,
                          poId: po?.id || 0,
                          vendorId: po?.vendorId || 0,
                          vendorName: po ? state.vendors.find((v) => v.id === po.vendorId)?.name || '' : '',
                          poQuantity: po ? po.items.reduce((sum, item) => sum + item.quantity, 0) : 0,
                          poAmount: po?.totalAmount || 0,
                          amount: po?.totalAmount || 0,
                        }));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select PO" />
                      </SelectTrigger>
                      <SelectContent>
                        {availablePOs.map((po, idx) => (
                          <SelectItem key={`${po.id}-${po.poNumber}-${idx}`} value={po.poNumber}>
                            {po.poNumber} - {state.vendors.find((v) => v.id === po.vendorId)?.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="vendorName">Vendor *</Label>
                    <Input
                      id="vendorName"
                      value={formData.vendorName}
                      onChange={(e) => setFormData((prev) => ({ ...prev, vendorName: e.target.value }))}
                      disabled
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="invoiceDate">Invoice Date *</Label>
                    <Input
                      id="invoiceDate"
                      type="date"
                      value={formData.invoiceDate}
                      onChange={(e) => setFormData((prev) => ({ ...prev, invoiceDate: e.target.value }))}
                    />
                  </div>

                  {/* Inline Items section under Vendor/Invoice Date when a PO is selected */}
                  {formData.poNumber && (
                    <div className="md:col-span-2">
                      <div className="mb-2">
                        <h4 className="font-semibold">Invoice Items</h4>
                        <p className="text-sm text-muted-foreground">Add items from the selected PO or create custom items</p>
                      </div>
                      <div className="border rounded-md p-3 space-y-3">
                        {/* Available PO Items - Show simplified list first */}
                        {selectedPOItems.length > 0 && (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <h5 className="font-medium text-sm">Available PO Items ({selectedPOItems.length})</h5>
                            </div>
                            
                            {/* Simplified PO Items List */}
                            <div className="space-y-1 max-h-32 overflow-y-auto border rounded-md p-2 bg-muted/30">
                              {selectedPOItems.map((poItem, index) => {
                                const sku = state.skus.find((s) => s.id === poItem.skuId);
                                const isAdded = formData.items.some(i => i.skuId === poItem.skuId);
                                return (
                                  <div key={poItem.skuId} className={`flex items-center justify-between p-2 rounded text-sm ${isAdded ? 'bg-green-50 text-green-700' : 'hover:bg-muted/50'}`}>
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs text-muted-foreground">#{index + 1}</span>
                                      <span className="font-medium">{sku?.name || 'Unknown Item'}</span>
                                      <span className="text-muted-foreground">({poItem.quantity} × {formatCurrency(poItem.unitPrice)})</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {isAdded && <span className="text-xs text-green-600 font-medium">✓ Added</span>}
                                      {!isAdded && (
                                        <Button 
                                          size="sm" 
                                          variant="ghost" 
                                          onClick={() => addItemFromPO(poItem)}
                                          className="h-6 px-2 text-xs"
                                        >
                                          Add
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex flex-wrap items-center gap-2 justify-between">
                          <div className="flex items-center gap-2">
                            <Button size="sm" onClick={addCustomItem}>Add Custom Item</Button>
                          </div>
                          {formData.items.length > 0 && (
                            <Button size="sm" variant="destructive" onClick={() => setFormData(prev => ({...prev, items: []}))}>Clear All Items</Button>
                          )}
                        </div>

                        {/* Added Items - Show detailed table */}
                        {formData.items.length > 0 && (
                          <div className="space-y-2">
                            <h5 className="font-medium text-sm">Added Items ({formData.items.length})</h5>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead className="border-b">
                                  <tr className="text-left">
                                    <th className="py-2 px-2">Item Name</th>
                                    <th className="py-2 px-2">SKU Code</th>
                                    <th className="py-2 px-2">Quantity</th>
                                    <th className="py-2 px-2">Unit Price</th>
                                    <th className="py-2 px-2">Total</th>
                                    <th className="py-2 px-2">Actions</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y">
                                  {formData.items.map((item) => (
                                    <tr key={item.id}>
                                      <td className="py-2 px-2">
                                        <Input value={item.skuName} onChange={e=>updateItem(item.id,'skuName',e.target.value)} />
                                      </td>
                                      <td className="py-2 px-2">
                                        <Input value={item.skuCode} onChange={e=>updateItem(item.id,'skuCode',e.target.value)} />
                                      </td>
                                      <td className="py-2 px-2 w-28">
                                        <Input type="number" value={item.quantity} onChange={e=>{ const qty = parseInt(e.target.value)||0; updateItemWithTotal(item.id,{quantity:qty}); }} />
                                      </td>
                                      <td className="py-2 px-2 w-32">
                                        <Input type="number" step="0.01" value={item.unitPrice} onChange={e=>{ const price = parseFloat(e.target.value)||0; updateItemWithTotal(item.id,{unitPrice:price}); }} />
                                      </td>
                                      <td className="py-2 px-2 font-medium">{formatCurrency(item.totalPrice)}</td>
                                      <td className="py-2 px-2">
                                        <Button variant="ghost" size="sm" className="text-red-500" onClick={()=>removeItem(item.id)}>Delete</Button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Empty State */}
                        {formData.items.length === 0 && selectedPOItems.length === 0 && (
                          <div className="text-center py-6 text-muted-foreground">
                            <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                            <p>No PO items available or items added yet</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="poQuantity">PO Quantity</Label>
                    <Input
                      id="poQuantity"
                      type="number"
                      value={formData.poQuantity || ''}
                      onChange={(e) => setFormData((prev) => ({ ...prev, poQuantity: parseInt(e.target.value) || 0 }))}
                      disabled
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="receivedQuantity">Received Quantity</Label>
                    <Input
                      id="receivedQuantity"
                      type="number"
                      value={formData.receivedQuantity || ''}
                      onChange={(e) => setFormData((prev) => ({ ...prev, receivedQuantity: parseInt(e.target.value) || 0 }))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="invoicedQuantity">Invoiced Quantity</Label>
                    <Input
                      id="invoicedQuantity"
                      type="number"
                      value={formData.invoicedQuantity || ''}
                      onChange={(e) => setFormData((prev) => ({ ...prev, invoicedQuantity: parseInt(e.target.value) || 0 }))}
                      placeholder="Auto-calculated from items"
                    />
                    <p className="text-xs text-muted-foreground">
                      This will be automatically calculated based on the items added below
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <div className="flex items-center gap-2 p-3 border rounded-md bg-muted/50">
                      <Badge variant={formData.status === 'Created' ? 'default' : 'secondary'}>
                        {formData.status}
                      </Badge>
                      
                    </div>
                  </div>
                </div>


                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                    placeholder="Additional notes about the invoice..."
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>


            {/* Previously Created Invoices for Same PO-ID */}
            {formData.poNumber && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Previously Created Invoices for PO: {formData.poNumber}</CardTitle>
                  <CardDescription>
                    PO Total Quantity: <span className="font-semibold text-primary">{totalPOQuantity}</span> | 
                    Total Previously Invoiced: <span className="font-semibold text-primary">{totalInvoicedQuantity}</span> | 
                    Remaining: <span className="font-semibold text-primary">{remainingQuantity}</span>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {existingInvoicesForPO.length > 0 ? (
                  <div className="space-y-3">
                      {existingInvoicesForPO
                        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()) // Show newest first
                        .map((invoice, index) => (
                      <div key={invoice.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">
                                #{index + 1}
                              </Badge>
                          <div>
                            <p className="font-medium">{invoice.invoiceNumber}</p>
                            <p className="text-sm text-muted-foreground">
                              {invoice.vendorName} • {invoice.invoicedQuantity} items
                            </p>
                              </div>
                          </div>
                          <Badge variant={invoice.status === 'Approved' ? 'default' : 'secondary'}>
                            {invoice.status}
                          </Badge>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">{formatCurrency(invoice.totalAmount)}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(invoice.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  ) : (
                    <div className="text-center py-6 text-muted-foreground">
                      <p>No previous invoices found for this PO</p>
                    </div>
                  )}
                  
                  {/* PO Status Calculation Preview */}
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-semibold text-blue-900 mb-2">PO Status Calculation</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span>PO Total Quantity:</span>
                        <span className="font-medium">{totalPOQuantity}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Previously Invoiced:</span>
                        <span className="font-medium">{totalInvoicedQuantity}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>This Invoice Quantity:</span>
                        <span className="font-medium text-primary">{formData.invoicedQuantity || 0}</span>
                      </div>
                      <div className="flex justify-between border-t pt-1">
                        <span>Total After This Invoice:</span>
                        <span className="font-medium text-primary">{totalInvoicedQuantity + (formData.invoicedQuantity || 0)}</span>
                      </div>
                      <div className="flex justify-between border-t pt-1">
                        <span className="font-semibold">PO Status After Save:</span>
                        <Badge 
                          variant={
                            totalInvoicedQuantity + (formData.invoicedQuantity || 0) === totalPOQuantity ? 'default' :
                            totalInvoicedQuantity + (formData.invoicedQuantity || 0) < totalPOQuantity ? 'secondary' :
                            'destructive'
                          }
                          className="font-semibold"
                        >
                          {totalInvoicedQuantity + (formData.invoicedQuantity || 0) === totalPOQuantity ? 'FULLY RECEIVED' :
                           totalInvoicedQuantity + (formData.invoicedQuantity || 0) < totalPOQuantity ? 'PARTIALLY RECEIVED' :
                           'OVER INVOICED'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  
                  {remainingQuantity <= 0 && (
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        ⚠️ All quantities for this PO have been invoiced. You may need to create a new PO for additional items.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Items Tab removed to avoid duplication (content lives in Basic Info) */}
          

          {/* Summary Tab */}
          <TabsContent value="summary" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Invoice Summary</CardTitle>
                <CardDescription>Review the invoice details before saving</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold mb-3">Invoice Details</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Invoice Number</span>
                        <span className="font-medium">{formData.invoiceNumber}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">PO Number</span>
                        <span className="font-medium">{formData.poNumber}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Vendor</span>
                        <span className="font-medium">{formData.vendorName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Invoice Date</span>
                        <span className="font-medium">
                          {new Date(formData.invoiceDate).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Due Date</span>
                        <span className="font-medium">
                          {new Date(formData.dueDate).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Payment Terms</span>
                        <span className="font-medium">{formData.paymentTerms}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-3">Financial Summary</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Subtotal</span>
                        <span className="font-medium">{formatCurrency(formData.subtotal)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Tax (10%)</span>
                        <span className="font-medium">{formatCurrency(formData.taxAmount)}</span>
                      </div>
                      <div className="flex justify-between text-lg font-semibold border-t pt-2">
                        <span>Total Amount</span>
                        <span className="text-primary">{formatCurrency(formData.totalAmount)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-3">Quantity Summary</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">PO Quantity</span>
                      <span className="font-medium">{totalPOQuantity}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Received Quantity</span>
                      <span className="font-medium">{formData.receivedQuantity}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Previously Invoiced</span>
                      <span className="font-medium">{totalInvoicedQuantity}</span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="text-muted-foreground">This Invoice Quantity</span>
                      <span className="font-medium text-primary">{formData.invoicedQuantity}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Remaining Quantity</span>
                      <span className="font-medium">{remainingQuantity - formData.invoicedQuantity}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-3">Items Summary</h4>
                  <div className="space-y-2">
                    {formData.items.map((item) => (
                      <div key={item.id} className="flex justify-between items-center p-2 bg-muted/50 rounded">
                        <div>
                          <p className="font-medium text-sm">{item.skuName}</p>
                          <p className="text-xs text-muted-foreground">{item.skuCode}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">{formatCurrency(item.totalPrice)}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.quantity} × {formatCurrency(item.unitPrice)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {formData.notes && (
                  <div>
                    <h4 className="font-semibold mb-2">Notes</h4>
                    <p className="text-sm bg-muted/50 p-3 rounded">{formData.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
          </div>

          {/* Fixed Footer */}
          <div className="shrink-0 border-t bg-background p-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Invoice
              </>
            )}
          </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};